import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { User } from "@/api/types";

export const getUserDisplayName = (user: User): string => {
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return fullName || user.username || user.email;
};

export const getUserInitials = (user: User): string => {
  const fallback = (user.username || user.email || "U").slice(0, 2).toUpperCase();
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || fallback;
};

type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
};

type UserTileProps = {
  user: User;
  disabled?: boolean;
  muted?: boolean;
  selected?: boolean;
  dashed?: boolean;
  className?: string;
  onClick?: () => void;
  cornerIcon?: IconProp;
  cornerIconClassName?: string;
  onActionClick?: () => void;
  actionIcon?: IconProp;
  actionAriaLabel?: string;
  checkbox?: CheckboxProps;
};

export default function UserTile({
  user,
  disabled = false,
  muted = false,
  selected = false,
  dashed = false,
  className = "",
  onClick,
  cornerIcon,
  cornerIconClassName = "text-gray-400",
  onActionClick,
  actionIcon,
  actionAriaLabel,
  checkbox,
}: UserTileProps) {
  const wrapperClassName = `relative rounded-lg border p-3 text-left ${
    dashed ? "border-dashed" : "border-solid"
  } ${
    selected
      ? "border-blue-300 bg-blue-50"
      : dashed
        ? "border-gray-300 bg-white"
        : "border-gray-200 bg-gray-50"
  } ${muted ? "opacity-60" : "opacity-100"} ${
    onClick ? "transition hover:opacity-100" : ""
  } ${className}`;

  const content = (
    <>
      {onActionClick && actionIcon && (
        <button
          type="button"
          onClick={onActionClick}
          disabled={disabled}
          className="absolute right-2 top-2 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
          aria-label={actionAriaLabel}
        >
          <FontAwesomeIcon icon={actionIcon} />
        </button>
      )}

      {cornerIcon && (
        <div className={`absolute right-2 top-2 rounded p-1 ${cornerIconClassName}`}>
          <FontAwesomeIcon icon={cornerIcon} />
        </div>
      )}

      <div className="flex items-center gap-2">
        {checkbox && (
          <input
            type="checkbox"
            checked={checkbox.checked}
            onChange={(event) => checkbox.onChange(event.target.checked)}
            disabled={disabled}
            aria-label={checkbox.label}
          />
        )}

        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-800">
          {getUserInitials(user)}
        </div>

        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-800">{getUserDisplayName(user)}</div>
          <div className="truncate text-xs text-gray-500">{user.email}</div>
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${wrapperClassName} ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      >
        {content}
      </button>
    );
  }

  return <div className={wrapperClassName}>{content}</div>;
}